// T-12.3: single source of truth for the team page and each member's author
// page (/team/<slug>). Field names match what LeaderSpotlight (TeamPage.jsx)
// already renders (image_url, linkedin_url, badge_label, years_experience,
// ...) -- same shape the team_members API table / src/data/teamMembers.js
// fallback already used, carried over unchanged, so this plugs in with no
// visual change, just a guaranteed server-rendered first paint.
//
// Rules (same as lib/facts.ts): a field that isn't confirmed is `null` or
// omitted, never a placeholder. `certificationVerifyUrl` is the one gate for
// the "GHL Certified Admin" badge: without it, no badge renders, because the
// badge specifically claims a verifiable credential.

export type TeamMember = {
  slug: string
  id: string
  name: string
  role: string
  badgeLabel: string | null
  badgeIcon: 'star' | 'rocket' | null
  /** e.g. "10+". Already published on the live site before Phase 0/1. */
  yearsExperience: string | null
  description: string
  image: string
  linkedin: string | null
  facebook: string | null
  upwork: string | null
  /** Certification names already stated on the live site. Empty = omit. */
  certifications: string[]
  /** Set ONLY once a verifiable credential URL exists. null = no badge. */
  certificationVerifyUrl: string | null
}

export const TEAM: TeamMember[] = [
  {
    slug: 'jewel-rana',
    id: 'jewel-rana',
    name: 'Jewel Rana',
    role: 'CEO & Co-Founder',
    badgeLabel: 'Visionary Leader',
    badgeIcon: 'star',
    yearsExperience: '10+',
    description:
      'Jewel Rana is an experienced business coach and agency leader who has helped freelancers and service providers build profitable careers and scalable service businesses.',
    image: '/CEO.png',
    linkedin: 'https://www.linkedin.com/in/thejewelrana/',
    facebook: 'https://www.facebook.com/thenewjewel',
    upwork: 'https://www.upwork.com/freelancers/~013caf34b8df0444cf/',
    certifications: [],
    certificationVerifyUrl: null,
  },
  {
    slug: 'niyamul-islam-sajal',
    id: 'niyamul-islam-sajal',
    name: 'Niyamul Islam Sajal',
    role: 'COO & Co-Founder',
    badgeLabel: 'Automation Expert',
    badgeIcon: 'rocket',
    yearsExperience: '3+',
    description:
      'Niyamul Islam Sajal is a senior automation engineer with extensive experience building AI-powered systems, CRM infrastructures, and custom integrations for agencies.',
    image: '/COO.png',
    linkedin: 'https://www.linkedin.com/in/niyamulislam/',
    facebook: 'https://www.facebook.com/niaymul.islam.2025/',
    upwork: 'https://www.upwork.com/freelancers/~010f634a8b80365e7b',
    certifications: [],
    certificationVerifyUrl: null,
  },
]

export function teamMemberBySlug(slug: string): TeamMember | undefined {
  return TEAM.find((m) => m.slug === slug)
}

/** True when a blog post's byline names a known team member (not the
 *  generic "GHL Prime Team" org byline), so BlogPosting schema can
 *  reference that member's Person @id instead of inventing one. */
export function teamMemberByAuthorName(author?: string | null): TeamMember | undefined {
  if (!author) return undefined
  const normalized = author.trim().toLowerCase()
  if (!normalized || /^ghl prime( team)?$/i.test(normalized)) return undefined
  return TEAM.find((m) => m.name.toLowerCase() === normalized)
}

/** Shape LeaderSpotlight (TeamPage.jsx) already renders -- a thin view over
 *  TeamMember so the component doesn't need to change its field names. */
export function asLeaderCard(member: TeamMember) {
  return {
    id: member.id,
    name: member.name,
    role: member.role,
    badge_label: member.badgeLabel,
    badge_icon: member.badgeIcon,
    years_experience: member.yearsExperience,
    description: member.description,
    image_url: member.image,
    linkedin_url: member.linkedin,
    facebook_url: member.facebook,
    instagram_url: '',
    twitter_url: '',
    upwork_url: member.upwork,
    website_url: '',
  }
}
